<script lang="ts">
	import type { z } from 'zod';

	export let label: string;
	export let id: string;
	export let placeholder: string | undefined = undefined;
	export let value: string | undefined = undefined;
	$: if (value) validate();
	export let required: boolean | null = null;
	export let errors: string[] | undefined = undefined;
	export let type: 'text' | 'email' | 'password' | 'file' = 'text';
	export let files: Array<File> | undefined = undefined;
	export let bottomRightLabel: string = '';
	export let multiple: boolean = false;
	export let accept: string[] | undefined = undefined;
	export let schema: z.ZodTypeAny | undefined = undefined;
	export let disabled: boolean | undefined = undefined;
	export let description: string | undefined = undefined;

	let filesInternal: FileList;
	$: files = filesInternal ? Array.from(filesInternal) : [];
	/**
	 * let consumer bypass any internal requirements for displaying errors
	 */
	export let forceShowErrors = false;

	const classList = 'input input-bordered w-full max-w-lg rounded-full';
	let showErrors = false;
	let element: HTMLInputElement | null = null;
	const validate = () => {
		if ((!forceShowErrors && !showErrors) || !schema) return;
		const result = schema.safeParse(type === 'file' ? files : element?.value);
		if (!result.success) {
			errors = result.error.flatten().formErrors;
		} else errors = undefined;
	};
</script>

<div class="form-control w-full max-w-lg">
	{#if description || required}
		<label class="label flex flex-col items-start" for={id}>
			<span class="label-text text-lg font-bold">
				{label}
				{#if required === true}
					<span class:text-error={required && errors && !value && !files}>
						*
						<span class="sr-only">Required</span>
					</span>
				{:else if required === false}
					<span class="text-opacity-80">(optional)</span>
				{:else}
					<span class="sr-only" />
				{/if}
			</span>
			{#if description}
				<span class="label-text font-light opacity-75">{description}</span>
			{/if}
		</label>
	{/if}
	<!-- this branching hack is needed to get 
		dynamic input types AND value binding -->
	{#if type === 'password'}
		<input
			{required}
			{id}
			name={id}
			bind:value
			type="password"
			{placeholder}
			{disabled}
			class:input-error={errors && (showErrors || forceShowErrors)}
			class={classList}
			on:blur|once={() => (showErrors = true)}
			on:blur={validate}
			bind:this={element}
			on:input={validate}
			on:paste={validate}
			on:change={validate}
			on:blur
			on:change
			on:input
		/>
	{:else if type === 'email'}
		<input
			{required}
			{id}
			name={id}
			bind:value
			type="email"
			{placeholder}
			{disabled}
			class:input-error={errors && (showErrors || forceShowErrors)}
			class={classList}
			on:blur|once={() => (showErrors = true)}
			on:blur={validate}
			bind:this={element}
			on:input={validate}
			on:paste={validate}
			on:blur
			on:change
			on:input
		/>
	{:else if type === 'text'}
		<input
			{required}
			{id}
			name={id}
			bind:value
			type="text"
			{placeholder}
			{disabled}
			class:input-error={errors && (showErrors || forceShowErrors)}
			class={classList}
			on:blur|once={() => (showErrors = true)}
			on:blur={validate}
			bind:this={element}
			on:input={validate}
			on:paste={validate}
			on:change={validate}
			on:blur
			on:change
			on:input
		/>
	{:else if type === 'file'}
		<input
			{required}
			{id}
			name={id}
			type="file"
			bind:files={filesInternal}
			{multiple}
			{disabled}
			class:input-error={errors && (showErrors || forceShowErrors)}
			accept={accept ? accept.join(', ') : '*'}
			class="file-input-bordered file-input w-full max-w-lg rounded-full"
			on:blur|once={() => (showErrors = true)}
			on:blur={validate}
			bind:this={element}
			on:change={validate}
			on:input={validate}
			on:blur
			on:change
			on:input
		/>
	{/if}
	{#if errors && (showErrors || forceShowErrors)}
		<label class="label" for={id}>
			<span class="label-text-alt">
				<ul class="label-text-alt p-1">
					{#if errors && (showErrors || forceShowErrors)}
						{#each errors as error}
							<li class="text-xs text-error">{error}</li>
						{/each}
					{:else}
						<li class="text-xs text-error opacity-0">There are no errors</li>
					{/if}
				</ul>
			</span>
			<span class="label-text-alt self-start">{bottomRightLabel}</span>
		</label>
	{/if}
</div>
